/**
 * ScrollManager — session-scoped scroll restoration for client-side routes.
 *
 * Behaviour:
 *   • Visiting a route for the first time in the session → scroll to top.
 *   • Returning to a route you've already visited in this session → restore
 *     to wherever you last left it.
 *   • Hash anchors (e.g. /settings#you) are honoured by the browser/app — we
 *     stay out of the way.
 *   • State lives in module memory, so a hard reload clears it (intentional).
 *
 * Mount once near the router root.
 */
import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

export default function ScrollManager() {
  const location = useLocation();
  const positionsRef = useRef(new Map());
  const currentPathRef = useRef(location.pathname);

  // Disable browser's native scroll restoration so it doesn't race with ours.
  useEffect(() => {
    const prev = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    return () => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = prev;
      }
    };
  }, []);

  // Continuously record scroll position for the current path. Cheap — Map.set
  // is O(1) and the listener is passive.
  useEffect(() => {
    const onScroll = () => {
      positionsRef.current.set(currentPathRef.current, window.scrollY);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On every pathname change, restore (or scroll to top).
  // Many routes fetch their own data after mount, so document.scrollHeight can
  // be tiny on the first scrollTo and the requested Y gets clamped to 0.
  // We retry on a few rAF-spaced intervals until either the saved Y is
  // reachable or the deadline passes.
  useLayoutEffect(() => {
    // Don't fight hash anchors — let the browser / page handle them.
    if (location.hash) {
      currentPathRef.current = location.pathname;
      return;
    }

    // If the path didn't actually change (e.g. only search/state changed),
    // don't touch the scroll.
    if (currentPathRef.current === location.pathname) return;

    currentPathRef.current = location.pathname;
    const saved = positionsRef.current.get(location.pathname) ?? 0;

    // Top of page is trivial — no retry needed.
    if (saved === 0) {
      window.scrollTo(0, 0);
      return;
    }

    let cancelled = false;
    const targetPath = location.pathname;
    // Schedule retries: 0, 100, 250, 500, 1000, 1800 ms. Bail out if the
    // user has navigated away again or touched the scroll themselves.
    const delays = [0, 100, 250, 500, 1000, 1800];
    const initialUserY = window.scrollY;
    delays.forEach((d) => {
      window.setTimeout(() => {
        if (cancelled) return;
        if (currentPathRef.current !== targetPath) return;
        // User started scrolling on their own — respect that.
        if (window.scrollY !== initialUserY && window.scrollY !== 0 && d > 0) return;
        const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const reachable = Math.min(saved, maxY);
        if (window.scrollY !== reachable) {
          window.scrollTo({ top: reachable, left: 0, behavior: "auto" });
        }
      }, d);
    });
    return () => { cancelled = true; };
  }, [location.pathname, location.hash]);

  return null;
}
